import { type ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { bindIssueChatAiChatId } from './bindWatchAiChat';
import {
  buildChatContextChips,
  CHAT_CHIP_KINDS,
  messagesForChat,
} from './marketingOpsPaths';
import WatchAiChatDialog from './WatchAiChatDialog';
import { issueChatScreen } from './watchChatScreen';

import type { ChatChipKind } from './marketingOpsPaths';
import type { WatchFinding, WatchTowerMockState } from './types';

import styles from './scss/TargetedMarketing.module.scss';

interface IIssueChatDialog {
  open: boolean,
  chatId: number,
  finding: WatchFinding,
  state: WatchTowerMockState,
  onClose: () => void,
  onApply: (next: WatchTowerMockState) => void,
}

const CHIP_I18N: Record<ChatChipKind, string> = {
  finding: 'chipFinding',
  source: 'chipSource',
  products: 'chipProducts',
  buildings: 'chipBuildings',
  knownBuyers: 'chipKnownBuyers',
  otherFindings: 'chipOtherFindings',
  pathASeed: 'chipPathASeed',
};

function IssueChatDialog({
  open,
  chatId,
  finding,
  state,
  onClose,
  onApply,
}: IIssueChatDialog): ReactElement {
  const { t } = useTranslation();
  const [chips, setChips] = useState<ChatChipKind[]>(['finding', 'source']);
  const chat = state.issueChats.find((item) => item.id === chatId);
  const messages = useMemo(
    () => messagesForChat(state, chatId),
    [chatId, state],
  );
  const screen = useMemo(
    () => issueChatScreen(
      finding,
      buildChatContextChips(state, finding, chips),
      messages,
    ),
    [chips, finding, messages, state],
  );

  const onToggleChip = (kind: ChatChipKind, checked: boolean) => {
    if (checked) {
      setChips((prev) => (prev.includes(kind) ? prev : [...prev, kind]));
      return;
    }
    setChips((prev) => prev.filter((item) => item !== kind));
  };

  return (
    <WatchAiChatDialog
      open={open}
      title={chat?.title || t('targetedMarketing.openIssueChat')}
      description={t('targetedMarketing.chatHint')}
      nestChatId={chat?.aiChatId ?? null}
      screen={screen}
      onClose={onClose}
      onChatCreated={(aiChatId) => {
        onApply(bindIssueChatAiChatId(state, chatId, aiChatId));
        toast.success(t('targetedMarketing.chatBound'));
      }}
      extraSlot={null}
      contextSlot={(
        <fieldset className={`${styles.field} ${styles.checkFieldset}`}>
          <legend>{t('targetedMarketing.chatChips')}</legend>
          <div className={styles.checkList}>
            {CHAT_CHIP_KINDS.map((kind) => (
              <label key={kind} className={styles.checkItem} htmlFor={`wt-chip-${kind}`}>
                <input
                  id={`wt-chip-${kind}`}
                  type="checkbox"
                  checked={chips.includes(kind)}
                  onChange={(event) => onToggleChip(kind, event.target.checked)}
                />
                {t(`targetedMarketing.${CHIP_I18N[kind]}`)}
              </label>
            ))}
          </div>
        </fieldset>
      )}
    />
  );
}

export default IssueChatDialog;
